import { CODEC_MSE_H264_RECODIFICADO, mimeMse } from '@film/shared';
import type { ClaseFuente, FuenteReproduccion } from './api.js';

/** El motor del reproductor integrado (D-26): pide el vídeo a la API como MP4 fragmentado
 *  y se lo da al `<video>` por Media Source Extensions, trozo a trozo.
 *
 *  Por qué MSE y no `<video src="…/stream">`: la respuesta del stream no tiene longitud
 *  —ffmpeg escribe según lee— y el `<video>` a secas no puede saltar dentro de algo así:
 *  ni sabe cuánto dura, ni puede pedir «desde el minuto 40». Con MSE somos nosotros quienes
 *  pedimos cada tramo, con el segundo de inicio en la URL, y quienes le decimos al vídeo
 *  cuánto dura la película. Y como el stream sale con los tiempos ABSOLUTOS del fichero,
 *  lo que llega se coloca solo donde le toca: no hay ningún desplazamiento que calcular. */

export interface SeleccionStream {
  fuente: ClaseFuente;
  audio: number;
  /** Subtítulo de imagen a incrustar. Los de texto no van en el stream. */
  subtituloIncrustado: number | null;
}

export function urlStream(downloadId: string, sel: SeleccionStream, desde: number): string {
  const q = new URLSearchParams({ fuente: sel.fuente, audio: String(sel.audio) });
  if (sel.subtituloIncrustado !== null) q.set('subtitulo', String(sel.subtituloIncrustado));
  if (desde > 0) q.set('t', desde.toFixed(3));
  return `/api/player/${encodeURIComponent(downloadId)}/stream?${q.toString()}`;
}

export function urlSubtitulo(
  downloadId: string,
  fuente: ClaseFuente,
  indice: number,
  ventana: { desde: number; hasta: number | null },
): string {
  const q = new URLSearchParams({ fuente });
  if (ventana.desde > 0) q.set('desde', ventana.desde.toFixed(3));
  if (ventana.hasta !== null) q.set('hasta', ventana.hasta.toFixed(3));
  return `/api/player/${encodeURIComponent(downloadId)}/subtitles/${indice}?${q.toString()}`;
}

/** La cadena que MSE exige antes del primer byte. Incrustar un subtítulo obliga a
 *  recodificar el vídeo, así que ahí la cadena es la de libx264 aunque el original se
 *  pudiera copiar. `null` si esa combinación no existe. */
export function mimeDe(fuente: FuenteReproduccion, audio: number, incrustar: boolean): string | null {
  if (!fuente.video) return null;
  const a = fuente.audio.find((x) => x.indice === audio);
  if (!a) return null;
  const video = incrustar || fuente.video.entrega === 'recodificar' ? CODEC_MSE_H264_RECODIFICADO : fuente.video.codecMse;
  return mimeMse(video, a.codecMse);
}

/** Lo mínimo de `TimeRanges` que hace falta, para poder probar sin navegador. */
export interface Rangos {
  length: number;
  start(i: number): number;
  end(i: number): number;
}

/** ¿El segundo `t` está dentro de lo ya cargado? Con un margen, porque el vídeo se posiciona
 *  con una precisión de fotograma y el rango empieza en un fotograma clave. */
export function cargado(rangos: Rangos, t: number, margen = 0.3): boolean {
  for (let i = 0; i < rangos.length; i += 1) {
    if (t >= rangos.start(i) - margen && t <= rangos.end(i) - 0.01) return true;
  }
  return false;
}

/** Cuántos segundos hay cargados por delante de `t`, dentro de su mismo tramo. Cero si `t`
 *  no está en ninguno: eso es que hay que pedir el stream desde ahí. */
export function cargadoPorDelante(rangos: Rangos, t: number): number {
  for (let i = 0; i < rangos.length; i += 1) {
    if (t >= rangos.start(i) - 0.3 && t <= rangos.end(i)) return rangos.end(i) - t;
  }
  return 0;
}

export function finDe(rangos: Rangos): number {
  return rangos.length ? rangos.end(rangos.length - 1) : 0;
}

/** El tramo cargado que contiene `t`, o `null`. */
export function tramoQueContiene(rangos: Rangos, t: number, margen = 0.5): { inicio: number; fin: number } | null {
  for (let i = 0; i < rangos.length; i += 1) {
    if (t >= rangos.start(i) - margen && t <= rangos.end(i) + margen) {
      return { inicio: rangos.start(i), fin: rangos.end(i) };
    }
  }
  return null;
}

/** Cuánto hay que leer del stream, o si hay que esperar.
 *
 *  `frontera` es hasta dónde ha llegado el stream en curso. Lo que se mide es lo que hay
 *  cargado entre el punto de reproducción y esa frontera, y solo si el punto de
 *  reproducción está EN ese tramo: si quien mira ha vuelto a otro sitio, el stream se queda
 *  parado —no se corta, por si vuelve— en vez de seguir convirtiendo una película que
 *  ahora mismo nadie está viendo. Sin esto, saltar al minuto 60 y volver al 1 dejaba a
 *  ffmpeg leyendo los seis gigas hasta el final. Con nada cargado aún, se lee. */
export function hayQueEsperar(rangos: Rangos, frontera: number, t: number, maximo = ADELANTO_MAX_S): boolean {
  const tramo = tramoQueContiene(rangos, frontera);
  if (!tramo) return false;
  // Detrás del tramo: se volvió a otro sitio.
  if (t < tramo.inicio - 0.5) return true;
  // Por delante del tramo hay dos casos. Si ahí hay OTRO tramo cargado, se fue a él y este
  // stream espera. Si no hay nada, es que el stream aún no ha llegado: copiando, arranca en
  // el fotograma clave anterior al segundo pedido, y durante unos segundos quien mira está
  // más allá de lo que hay. Eso es leer, no esperar; esperar ahí dejaba el salto clavado con
  // dos segundos cargados detrás del punto de reproducción.
  if (t > tramo.fin && tramoQueContiene(rangos, t, 0)) return true;
  return tramo.fin - t > maximo;
}

/** Cuánto se deja cargar por delante de donde se está viendo. No es memoria: es CPU. Un
 *  ffmpeg recodificando va tan rápido como puede, y sin freno convertiría la película
 *  entera en cuanto se abre; dejando de leer el stream, la tubería se llena y ffmpeg se
 *  para solo hasta que haga falta más. Minuto y medio aguanta un tropiezo de red y no
 *  tiene a la máquina trabajando para nada. */
export const ADELANTO_MAX_S = 90;

/** Al llenarse la memoria del `<video>`, cuánto se conserva por detrás del punto de
 *  reproducción. Volver atrás dentro de ese margen no pide nada al servidor. */
const CONSERVAR_DETRAS_S = 30;

/** Cuántos bytes se aceptan sin que `buffered` avance por donde va el stream antes de dar
 *  la sesión por rota. El freno de arriba mide lo cargado en `video.buffered` alrededor de la
 *  frontera del stream; si el navegador no coloca ahí lo que llega —no reconoce ningún
 *  fotograma clave, o el fichero no empieza en el segundo 0 y lo pone en otro sitio— el
 *  freno no salta nunca y se descarga la película ENTERA hasta que el navegador tira un
 *  `QuotaExceededError` que no puede resolver, porque no hay nada detrás del punto de
 *  reproducción que soltar. Pasó con un H.264 copiado en Firefox: 1,18 GB. Es holgado a
 *  propósito: copiando, el stream arranca en el fotograma clave anterior al segundo pedido,
 *  que puede quedar diez segundos —decenas de megas en un remux— por delante de la frontera. */
export const BYTES_SIN_COLOCAR_MAX = 96 * 1024 * 1024;

/** Hasta cuánto más adelante del segundo pedido se acepta que empiece lo que llega, para
 *  llevar allí al vídeo. Un grupo de imágenes descartado son unos segundos; más que esto ya
 *  no es «el principio que el navegador tiró», es otra cosa, y se deja el error de arriba. */
export const RECOLOCAR_MAX_S = 30;

/** `TimeRanges` como lista, para poder comparar dos fotos. */
export function tramosDe(rangos: Rangos): Tramo[] {
  const salida: Tramo[] = [];
  for (let i = 0; i < rangos.length; i += 1) salida.push({ inicio: rangos.start(i), fin: rangos.end(i) });
  return salida;
}

/** El tramo que ha cambiado entre dos fotos de `buffered`: el que ha crecido o el que ha
 *  aparecido. Es donde escribe el stream en curso, y se busca así y no «alrededor de donde
 *  se pidió» porque el navegador no siempre coloca lo que llega donde se pidió: un fichero
 *  cuyo primer grupo de imágenes no empieza por un fotograma clave que acepte lo descarta
 *  hasta el siguiente, y lo cargado empezaba en el 4,8 mientras se buscaba en el 0. Si dos
 *  tramos se funden, el fundido es el nuevo. `null` si nada cambió: la cabecera, o medio
 *  fragmento. */
export function tramoQueCrecio(antes: Tramo[], despues: Tramo[], epsilon = 0.001): Tramo | null {
  const igual = (a: Tramo, b: Tramo) => Math.abs(a.inicio - b.inicio) < epsilon && Math.abs(a.fin - b.fin) < epsilon;
  return despues.find((d) => !antes.some((a) => igual(a, d))) ?? null;
}

/** Los tramos cargados como texto, para un mensaje de error que diga dónde está lo que llegó. */
export function describirRangos(rangos: Rangos): string {
  if (!rangos.length) return 'nada';
  const partes: string[] = [];
  for (let i = 0; i < rangos.length; i += 1) partes.push(`${rangos.start(i).toFixed(1)}–${rangos.end(i).toFixed(1)} s`);
  return partes.join(', ');
}

export interface OpcionesSesion {
  /** La URL del stream desde el segundo que se pida. */
  url: (desde: number) => string;
  mime: string;
  duracionSegundos: number | null;
  onError: (mensaje: string) => void;
}

/** Una película abierta en un `<video>`: un `MediaSource`, un `SourceBuffer` y, en cada
 *  momento, como mucho UNA petición al stream. Saltar a un punto que no está cargado
 *  corta la petición en curso —y con ella el ffmpeg del servidor— y abre otra desde ahí. */
export class SesionReproduccion {
  private readonly ms: MediaSource;
  private sb: SourceBuffer | null = null;
  private control: AbortController | null = null;
  private readonly objectUrl: string;
  private cerrada = false;
  /** Hasta dónde ha llegado el stream en curso. Arranca en el segundo pedido y avanza con
   *  cada trozo añadido: es lo que dice si quien mira sigue en este tramo o se fue a otro. */
  private frontera = 0;

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly opts: OpcionesSesion,
  ) {
    this.ms = new MediaSource();
    this.objectUrl = URL.createObjectURL(this.ms);
  }

  static disponible(mime: string): boolean {
    return typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported(mime);
  }

  async abrir(desde: number): Promise<void> {
    const abierto = new Promise<void>((resolver) =>
      this.ms.addEventListener('sourceopen', () => resolver(), { once: true }),
    );
    this.video.src = this.objectUrl;
    await abierto;
    if (this.cerrada) return;

    // La duración se la decimos nosotros: el stream no la trae, y sin ella la barra del
    // vídeo no tendría dónde saltar.
    if (this.opts.duracionSegundos) this.ms.duration = this.opts.duracionSegundos;
    this.sb = this.ms.addSourceBuffer(this.opts.mime);
    this.sb.mode = 'segments';

    this.video.addEventListener('seeking', this.alBuscar);
    this.video.addEventListener('waiting', this.alAtascarse);
    this.video.currentTime = desde;
    // No se espera a la carga: dura lo que dure el stream, es decir, la película entera.
    // Abrir es dejar el vídeo pidiendo; cómo va lo cuentan sus propios eventos.
    void this.cargarDesde(desde);
  }

  cerrar(): void {
    if (this.cerrada) return;
    this.cerrada = true;
    this.control?.abort();
    this.video.removeEventListener('seeking', this.alBuscar);
    this.video.removeEventListener('waiting', this.alAtascarse);
    try {
      if (this.sb && this.ms.readyState === 'open') this.sb.abort();
    } catch {
      // Ya cerrado: no hay nada que abortar.
    }
    try {
      if (this.ms.readyState === 'open') this.ms.endOfStream();
    } catch {
      // Lo mismo.
    }
    this.video.removeAttribute('src');
    this.video.load();
    URL.revokeObjectURL(this.objectUrl);
  }

  /** Solo se pide al servidor lo que no se tiene: un salto dentro de lo cargado lo
   *  resuelve el propio `<video>`. */
  private readonly alBuscar = (): void => {
    const t = this.video.currentTime;
    if (cargado(this.video.buffered, t)) return;
    void this.cargarDesde(t);
  };

  /** El vídeo se ha quedado sin datos donde está. Si es en la frontera del stream en curso
   *  es que la red o ffmpeg van justos, y lo único que cabe es esperar; si es en cualquier
   *  otro sitio —se volvió a un tramo cargado antes y se llegó a su final— el stream que
   *  hay está en otra parte y hay que pedir uno desde aquí. Sin esto, volver atrás y
   *  seguir viendo se paraba al acabarse lo cargado, sin saltar y sin que nadie pidiera
   *  nada. */
  private readonly alAtascarse = (): void => {
    const t = this.video.currentTime;
    if (Math.abs(t - this.frontera) < 2) return;
    if (cargado(this.video.buffered, t)) return;
    void this.cargarDesde(t);
  };

  private async cargarDesde(desde: number): Promise<void> {
    if (this.cerrada || !this.sb) return;
    this.control?.abort();
    const control = new AbortController();
    this.control = control;

    // Cortar una carga a medias deja al analizador de MSE con medio fragmento dentro: los
    // trozos de `fetch` no vienen alineados con las cajas del MP4, así que lo último que
    // se le dio casi nunca termina donde termina una. `abort()` tira eso y lo deja
    // esperando una cabecera nueva, y hay que llamarlo SIEMPRE, no solo si está
    // procesando: sin él, la cabecera del stream nuevo se pegaba detrás de la mitad de un
    // `mdat` viejo y Chrome moría con «Failed to prepare video sample for decode». Lo ya
    // cargado se queda, así que volver atrás sigue sin pedir nada.
    const sb = this.sb;
    if (this.ms.readyState === 'open') sb.abort();
    this.frontera = desde;

    let respuesta: Response;
    try {
      respuesta = await fetch(this.opts.url(desde), { credentials: 'same-origin', signal: control.signal });
    } catch (err) {
      if (!control.signal.aborted) this.opts.onError(`No se pudo pedir el vídeo: ${(err as Error).message}`);
      return;
    }
    if (control.signal.aborted) return;
    if (!respuesta.ok || !respuesta.body) {
      const cuerpo = (await respuesta.json().catch(() => null)) as { error?: { message?: string } } | null;
      this.opts.onError(cuerpo?.error?.message ?? `El servidor respondió ${respuesta.status}`);
      return;
    }

    await this.bombear(respuesta.body, sb, control.signal);
  }

  private async bombear(cuerpo: ReadableStream<Uint8Array>, sb: SourceBuffer, senal: AbortSignal): Promise<void> {
    const lector = cuerpo.getReader();
    senal.addEventListener('abort', () => void lector.cancel().catch(() => undefined), { once: true });
    const pedidoDesde = this.frontera;
    let bytesSinColocar = 0;
    let recolocado = false;
    try {
      for (;;) {
        await this.esperarHueco(senal);
        if (senal.aborted) return;
        const { value, done } = await lector.read();
        if (done) break;
        if (senal.aborted) return;
        const antes = tramosDe(sb.buffered);
        await this.anadir(sb, value, senal);
        const tramo = tramoQueCrecio(antes, tramosDe(sb.buffered));
        if (tramo) {
          this.frontera = tramo.fin;
          bytesSinColocar = 0;
          // El vídeo espera en el segundo pedido y lo que ha llegado empieza más adelante:
          // el navegador ha descartado el principio (ver `tramoQueCrecio`). Se le lleva al
          // primer dato, una vez por stream, y solo si quien mira sigue donde pidió; si no,
          // se quedaría parado para siempre con la película cargada cinco segundos más allá.
          const t = this.video.currentTime;
          if (
            !recolocado &&
            t < tramo.inicio &&
            t >= pedidoDesde - 0.5 &&
            tramo.inicio - pedidoDesde < RECOLOCAR_MAX_S &&
            !cargado(sb.buffered, t)
          ) {
            recolocado = true;
            this.video.currentTime = tramo.inicio;
          }
        } else {
          bytesSinColocar += value.byteLength;
          if (bytesSinColocar > BYTES_SIN_COLOCAR_MAX) {
            this.control?.abort();
            this.opts.onError(
              `El navegador no coloca lo que llega: se pidió el vídeo desde el segundo ${pedidoDesde.toFixed(1)}, ` +
                `han llegado ${Math.round(bytesSinColocar / 1024 / 1024)} MB y lo cargado está en ${describirRangos(sb.buffered)}. ` +
                'Se corta para no descargar la película entera. Puede que el fichero no empiece en el segundo 0 ' +
                'o que el navegador no reconozca sus fotogramas clave.',
            );
            return;
          }
        }
      }
      // Si el stream llegó hasta el final de la película, se le dice al vídeo para que
      // dispare `ended`. Si acabó antes —ffmpeg murió, o se cortó— no: cerrarlo ahí
      // acortaría la duración a lo que hubiera cargado.
      const duracion = this.opts.duracionSegundos;
      if (!senal.aborted && duracion && finDe(sb.buffered) >= duracion - 1.5 && this.ms.readyState === 'open') {
        await this.esperarLibre(sb);
        if (!senal.aborted && this.ms.readyState === 'open') this.ms.endOfStream();
      }
    } catch (err) {
      if (senal.aborted || this.cerrada) return;
      this.opts.onError(`Se cortó la reproducción: ${(err as Error).message}`);
    }
  }

  /** Añade un trozo y espera a que MSE lo haya tragado. Si el `<video>` dice que no le cabe
   *  más, se suelta lo que ya se vio y se vuelve a intentar una vez. */
  private async anadir(sb: SourceBuffer, trozo: Uint8Array, senal: AbortSignal): Promise<void> {
    await this.esperarLibre(sb);
    if (senal.aborted) return;
    try {
      sb.appendBuffer(trozo as BufferSource);
    } catch (err) {
      if ((err as DOMException).name !== 'QuotaExceededError') throw err;
      const hasta = Math.max(0, this.video.currentTime - CONSERVAR_DETRAS_S);
      if (hasta > 0) {
        sb.remove(0, hasta);
        await this.esperarLibre(sb);
      }
      if (senal.aborted) return;
      sb.appendBuffer(trozo as BufferSource);
    }
    await this.esperarLibre(sb);
  }

  private esperarLibre(sb: SourceBuffer): Promise<void> {
    if (!sb.updating) return Promise.resolve();
    return new Promise((resolver) => {
      const fin = () => {
        sb.removeEventListener('updateend', fin);
        sb.removeEventListener('abort', fin);
        sb.removeEventListener('error', fin);
        resolver();
      };
      sb.addEventListener('updateend', fin);
      sb.addEventListener('abort', fin);
      sb.addEventListener('error', fin);
    });
  }

  /** Deja de leer el stream mientras haya bastante cargado por delante, o mientras quien
   *  mira esté en otro tramo. Es el freno que hace que ffmpeg no convierta más de lo que se
   *  va a ver. */
  private async esperarHueco(senal: AbortSignal): Promise<void> {
    while (!senal.aborted && hayQueEsperar(this.video.buffered, this.frontera, this.video.currentTime)) {
      await new Promise<void>((resolver) => {
        const fin = () => {
          clearTimeout(reloj);
          this.video.removeEventListener('timeupdate', fin);
          this.video.removeEventListener('seeking', fin);
          senal.removeEventListener('abort', fin);
          resolver();
        };
        const reloj = setTimeout(fin, 1_000);
        this.video.addEventListener('timeupdate', fin);
        this.video.addEventListener('seeking', fin);
        senal.addEventListener('abort', fin);
      });
    }
  }
}

// ── Subtítulos de texto ──────────────────────────────────────────────────────────────────

/** Los subtítulos de texto no van como `<track>`: un `<track>` no pinta nada hasta tener el
 *  fichero ENTERO, y sacar la pista entera de un MKV de seis gigas es leerlo de cabo a rabo,
 *  minutos en un disco ocupado. Se piden por VENTANAS —de aquí a veinte minutos— que ffmpeg
 *  saca en segundos saltando por el índice del contenedor, se analizan según llegan y se
 *  añaden a una pista creada con `addTextTrack`, que sobrevive a los cambios de stream: poner
 *  otro audio no vuelve a pedir nada. */

/** Cuánto se pide de una vez. */
export const VENTANA_SUBTITULOS_S = 20 * 60;
/** Con menos de esto cubierto por delante, se pide la ventana siguiente. */
export const ADELANTO_SUBTITULOS_S = 120;

export interface Tramo {
  inicio: number;
  fin: number;
}

export interface Cue {
  inicio: number;
  fin: number;
  texto: string;
}

/** `hh:mm:ss.mmm` o `mm:ss.mmm` a segundos. */
export function segundosVtt(marca: string): number | null {
  const m = /^(?:(\d+):)?(\d{1,2}):(\d{2})\.(\d{3})$/.exec(marca.trim());
  if (!m) return null;
  const horas = m[1] ? Number(m[1]) : 0;
  return horas * 3600 + Number(m[2]) * 60 + Number(m[3]) + Number(m[4]) / 1000;
}

const LINEA_TIEMPOS = /^(\S+)\s+-->\s+(\S+)(?:\s+.*)?$/;

/** Analizador de WebVTT que traga el texto según llega, en trozos cortados por donde sea.
 *  Cada bloque es una línea de tiempos —con un identificador opcional delante— seguida del
 *  texto hasta la línea en blanco. Lo que no case se ignora: la cabecera, notas, estilos. */
export class LectorVtt {
  private resto = '';

  alimentar(trozo: string): Cue[] {
    this.resto += trozo.replace(/\r\n?/g, '\n');
    const cues: Cue[] = [];
    for (;;) {
      const corte = this.resto.indexOf('\n\n');
      if (corte < 0) break;
      const bloque = this.resto.slice(0, corte);
      this.resto = this.resto.slice(corte + 2);
      const cue = analizarBloque(bloque);
      if (cue) cues.push(cue);
    }
    return cues;
  }

  /** Lo que quedara sin línea en blanco detrás: el último bloque del fichero. */
  terminar(): Cue[] {
    const cue = analizarBloque(this.resto);
    this.resto = '';
    return cue ? [cue] : [];
  }
}

function analizarBloque(bloque: string): Cue | null {
  const lineas = bloque.split('\n').filter((l, i, todas) => !(i === todas.length - 1 && l === ''));
  const i = lineas.findIndex((l) => LINEA_TIEMPOS.test(l));
  if (i < 0) return null;
  const m = LINEA_TIEMPOS.exec(lineas[i]!)!;
  const inicio = segundosVtt(m[1]!);
  const fin = segundosVtt(m[2]!);
  if (inicio === null || fin === null || fin < inicio) return null;
  return { inicio, fin, texto: lineas.slice(i + 1).join('\n') };
}

/** Los tramos ya cubiertos más uno nuevo, fundidos y ordenados. */
export function unirTramos(tramos: Tramo[], nuevo: Tramo): Tramo[] {
  const todos = [...tramos, nuevo].sort((a, b) => a.inicio - b.inicio);
  const salida: Tramo[] = [];
  for (const t of todos) {
    const ultimo = salida[salida.length - 1];
    if (ultimo && t.inicio <= ultimo.fin + 0.001) ultimo.fin = Math.max(ultimo.fin, t.fin);
    else salida.push({ ...t });
  }
  return salida;
}

/** Qué ventana pedir ahora para quien está en `t`, o `null` si con lo cubierto basta.
 *  Si `t` no está cubierto, desde `t`; si lo está pero queda menos de `adelanto` por
 *  delante, la siguiente a continuación. Nunca más allá de la duración. */
export function ventanaSubtitulos(
  cubiertos: Tramo[],
  t: number,
  duracion: number | null,
  ventana = VENTANA_SUBTITULOS_S,
  adelanto = ADELANTO_SUBTITULOS_S,
): Tramo | null {
  const tope = (desde: number): number | null => {
    const fin = desde + ventana;
    if (duracion === null) return fin;
    return Math.min(fin, duracion);
  };
  const tramo = cubiertos.find((c) => t >= c.inicio && t <= c.fin);
  if (!tramo) {
    const fin = tope(t);
    return fin === null || fin <= t ? { inicio: t, fin: t + ventana } : { inicio: t, fin };
  }
  if (duracion !== null && tramo.fin >= duracion) return null;
  if (tramo.fin - t > adelanto) return null;
  const fin = tope(tramo.fin);
  return fin === null || fin <= tramo.fin ? null : { inicio: tramo.fin, fin };
}

export interface OpcionesPista {
  url: (ventana: { desde: number; hasta: number | null }) => string;
  duracionSegundos: number | null;
  etiqueta: string;
  idioma: string | null;
  onError: (mensaje: string) => void;
}

/** Un subtítulo de texto puesto sobre un `<video>`: la pista, lo que ya se ha pedido y la
 *  petición en curso. Se crea una por pista y se reutiliza: volver a ella no pide nada. */
export class PistaSubtitulos {
  private pista: TextTrack | null = null;
  private cubiertos: Tramo[] = [];
  private enCurso: { tramo: Tramo; control: AbortController } | null = null;
  private readonly vistas = new Set<string>();
  private activa = false;

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly opts: OpcionesPista,
  ) {}

  activar(): void {
    if (this.activa) return;
    this.activa = true;
    if (!this.pista) {
      this.pista = this.video.addTextTrack('subtitles', this.opts.etiqueta, this.opts.idioma ?? undefined);
    }
    this.pista.mode = 'showing';
    this.video.addEventListener('timeupdate', this.comprobar);
    this.video.addEventListener('seeking', this.comprobar);
    this.comprobar();
  }

  desactivar(): void {
    if (!this.activa) return;
    this.activa = false;
    this.video.removeEventListener('timeupdate', this.comprobar);
    this.video.removeEventListener('seeking', this.comprobar);
    this.enCurso?.control.abort();
    this.enCurso = null;
    if (this.pista) this.pista.mode = 'disabled';
  }

  private readonly comprobar = (): void => {
    if (!this.activa) return;
    const t = this.video.currentTime;
    // La petición en curso cuenta como cubierta mientras traiga lo que hace falta: si no,
    // cada `timeupdate` volvería a pedir lo mismo.
    const cubiertos = this.enCurso ? unirTramos(this.cubiertos, this.enCurso.tramo) : this.cubiertos;
    const ventana = ventanaSubtitulos(cubiertos, t, this.opts.duracionSegundos);
    if (!ventana) return;
    if (this.enCurso) {
      // Ya se está pidiendo algo, pero no sirve para donde se está ahora (un salto lejos):
      // se corta y se pide desde aquí. Si sirve, se espera a que acabe.
      const sirve = t >= this.enCurso.tramo.inicio && t <= this.enCurso.tramo.fin;
      if (sirve) return;
      this.enCurso.control.abort();
      this.enCurso = null;
    }
    void this.pedir(ventana);
  };

  private async pedir(tramo: Tramo): Promise<void> {
    const control = new AbortController();
    this.enCurso = { tramo, control };
    const hasta = this.opts.duracionSegundos !== null && tramo.fin >= this.opts.duracionSegundos ? null : tramo.fin;
    try {
      const respuesta = await fetch(this.opts.url({ desde: tramo.inicio, hasta }), {
        credentials: 'same-origin',
        signal: control.signal,
      });
      if (!respuesta.ok || !respuesta.body) {
        const cuerpo = (await respuesta.json().catch(() => null)) as { error?: { message?: string } } | null;
        this.opts.onError(cuerpo?.error?.message ?? `No se pudo pedir el subtítulo (${respuesta.status})`);
        return;
      }
      const lector = respuesta.body.getReader();
      const decodificador = new TextDecoder();
      const vtt = new LectorVtt();
      for (;;) {
        const { value, done } = await lector.read();
        if (done) break;
        this.anadir(vtt.alimentar(decodificador.decode(value, { stream: true })));
      }
      this.anadir(vtt.alimentar(decodificador.decode()));
      this.anadir(vtt.terminar());
      if (!control.signal.aborted) {
        this.cubiertos = unirTramos(this.cubiertos, hasta === null ? { ...tramo, fin: Number.POSITIVE_INFINITY } : tramo);
      }
    } catch (err) {
      if (!control.signal.aborted) this.opts.onError(`Se cortó el subtítulo: ${(err as Error).message}`);
    } finally {
      if (this.enCurso?.control === control) this.enCurso = null;
    }
    // Por si mientras llegaba ya toca la siguiente.
    if (this.activa) this.comprobar();
  }

  /** Las ventanas se solapan a propósito —el margen de antes, el fotograma clave anterior—,
   *  así que la misma entrada puede venir dos veces. */
  private anadir(cues: Cue[]): void {
    if (!this.pista) return;
    for (const c of cues) {
      const clave = `${c.inicio}|${c.fin}|${c.texto}`;
      if (this.vistas.has(clave)) continue;
      this.vistas.add(clave);
      this.pista.addCue(new VTTCue(c.inicio, c.fin, c.texto));
    }
  }
}
